import React, { useEffect, useRef, useState } from 'react';
import { Building2, Calendar, MapPin, ChevronRight } from 'lucide-react';

const TimelineItem = ({ job, index, isVisible }) => {
  const [expanded, setExpanded] = useState(index === 0);

  return (
    <div 
      className={`relative pl-8 md:pl-12 pb-12 last:pb-0 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
      style={{ animationDelay: `${index * 0.15}s` }}
    >
      {/* Timeline Line */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-[#d4af37] via-[#d4af37]/50 to-transparent" />
      
      {/* Timeline Dot */}
      <div className={`absolute left-0 top-0 w-4 h-4 -translate-x-1/2 transition-all duration-500 ${
        job.current 
          ? 'bg-[#d4af37] shadow-lg shadow-[#d4af37]/50' 
          : 'bg-[#1a2744] border-2 border-[#d4af37]'
      }`}>
        {job.current && (
          <div className="absolute inset-0 bg-[#d4af37] animate-ping opacity-50" />
        )}
      </div>

      {/* Year Badge */}
      <div className="absolute left-6 md:left-10 -top-1 bg-[#0a1628] px-2">
        <span className="text-[#d4af37] text-xs font-bold tracking-wider">
          {job.duration.split(' - ')[0]}
        </span>
      </div>

      {/* Card */}
      <div 
        className={`group bg-[#1a2744] border transition-all duration-500 cursor-pointer mt-6 ${
          job.current 
            ? 'border-[#d4af37]/40 shadow-lg shadow-[#d4af37]/10' 
            : 'border-[#d4af37]/10 hover:border-[#d4af37]/30'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Header */}
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-3 flex-1">
              {/* Current Badge */}
              {job.current && (
                <span className="inline-block bg-[#d4af37] text-[#0a1628] text-xs font-bold px-3 py-1 uppercase tracking-wider">
                  Current Role
                </span>
              )}
              
              {/* Title */}
              <h3 className="text-xl md:text-2xl font-bold text-white group-hover:text-[#d4af37] transition-colors">
                {job.title}
              </h3>
              
              {/* Company */}
              <div className="flex items-center gap-2 text-[#d4af37]">
                <Building2 size={16} />
                <span className="font-semibold">{job.company}</span>
              </div>
              
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#64748b]">
                <div className="flex items-center gap-1.5">
                  <Calendar size={14} />
                  <span>{job.duration}</span>
                  {job.totalDuration && (
                    <span className="text-[#d4af37] font-medium ml-1">• {job.totalDuration}</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin size={14} />
                  <span>{job.location}</span>
                </div>
              </div>
            </div>
            
            {/* Expand Icon */}
            <ChevronRight 
              size={24} 
              className={`text-[#64748b] group-hover:text-[#d4af37] transition-all duration-300 ${
                expanded ? 'rotate-90' : ''
              }`} 
            />
          </div>
        </div>

        {/* Expanded Content */}
        <div className={`overflow-hidden transition-all duration-500 ${
          expanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-6 pb-6 space-y-4 border-t border-[#d4af37]/10 pt-4">
            <p className="text-[#94a3b8] leading-relaxed">{job.description}</p>
            
            {/* Highlights */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-[#d4af37] uppercase tracking-wider">Key Responsibilities</h4>
              <ul className="grid md:grid-cols-2 gap-2">
                {job.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[#94a3b8] text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] mt-1.5 flex-shrink-0" />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Experience = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="journey" ref={sectionRef} className="py-24 md:py-32 bg-[#0a1628] relative">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="h-full w-full" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #d4af37 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className={`mb-16 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="text-[#d4af37] text-sm font-semibold uppercase tracking-[0.2em]">My Journey</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4">
            Career <span className="text-[#d4af37]">Timeline</span>
          </h2>
          <div className="w-20 h-1 bg-[#d4af37] mt-6" />
          <p className="text-[#94a3b8] mt-6 max-w-2xl text-lg">
            A progressive journey through India's leading financial institutions, driving digital transformation and product innovation.
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl ml-4 md:ml-8">
          {data.map((job, index) => (
            <TimelineItem key={job.id} job={job} index={index} isVisible={isVisible} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
